import { z } from "zod";
import { engineSchema, terminalStateSchema } from "./capture.js";
/**
 * The Prompts screen's read boundary.
 *
 * One row is one capture of one question on one engine on one day — not one
 * question. A question asked on two engines is two measurements and the screen
 * has to be able to disagree with itself between them, which a per-question row
 * cannot express.
 */
/** ISO date with no time, e.g. `2026-08-01`. The capture day. */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, "expected an ISO date, e.g. 2026-08-01");
/**
 * What the parser produced from the sealed page, or null when it produced
 * nothing.
 *
 * Nested rather than five nullable columns, so "this capture has no parsed
 * answer" is one check at the call site. A run that failed has no chars, no
 * citations and no fan-out — reporting those as zero would make a login wall
 * indistinguishable from an engine that answered and cited nobody
 * (AGENTS.md §1).
 */
export const parsedSummarySchema = z.object({
    parserName: z.string(),
    parserVersion: z.number().int(),
    /** `shipped` or `override` — which selector found the answer. */
    selectorSource: z.string(),
    citationCount: z.number().int().min(0),
    /** Links inside the answer text, not merely in a sources list. */
    inlineCitationCount: z.number().int().min(0),
    /**
     * Searches the engine disclosed it ran. Counted apart from `generated` and
     * never summed with it: one is a measurement and the other is our prediction,
     * and a single "fan-out" number would hide which you were looking at.
     */
    observedFanoutCount: z.number().int().min(0),
    generatedFanoutCount: z.number().int().min(0),
});
/** Where one brand landed in one answer. Absent means it was not named. */
export const rowMentionSchema = z.object({
    brandId: z.string(),
    brandName: z.string(),
    /** Ordinal position among the brands this answer names, from one. */
    rank: z.number().int().min(1),
    occurrences: z.number().int().min(1),
});
export const responseRowSchema = z.object({
    runId: z.string(),
    promptId: z.string(),
    promptText: z.string(),
    country: z.string(),
    language: z.string(),
    tags: z.array(z.string()),
    engine: z.string(),
    /** `truth` (a real browser) or `proxy` (an official API). Never averaged. */
    tier: z.string(),
    runDate: isoDate,
    attempt: z.number().int(),
    /** null while the capture is queued or running — it has no outcome yet. */
    terminalState: z.string().nullable(),
    /** True once the evidence manifest hash is stored against the run. */
    sealed: z.boolean(),
    parsed: parsedSummarySchema.nullable(),
    /**
     * Whether the companies in the answer have been read out of it yet: null
     * when there is no answer. Until it is `read`, an empty `mentions` means
     * "not known yet", never "left everyone out" (§1).
     */
    readState: z.enum(["read", "reading", "unreadable"]).nullable(),
    mentions: z.array(rowMentionSchema),
});
/**
 * Every filter the screen offers, and the page cursor.
 *
 * These are the querystring, and the browser keeps them in the URL rather than
 * in component state (AGENTS.md §15a) — so a filtered view can be sent to a
 * colleague and opens as the same view.
 */
export const responseFilterSchema = z.object({
    engine: engineSchema.optional(),
    country: z.string().trim().length(2).optional(),
    tag: z.string().trim().min(1).max(40).optional(),
    terminalState: terminalStateSchema.optional(),
    tier: z.enum(["truth", "proxy"]).optional(),
    /** Only captures that named this brand. */
    brandId: z.string().trim().min(1).optional(),
    /** Inclusive bounds on the capture day. */
    from: isoDate.optional(),
    to: isoDate.optional(),
    /** Case-insensitive substring of the question. */
    q: z.string().trim().min(1).max(200).optional(),
    /** Only the questions this report tracks. */
    reportId: z.string().trim().min(1).optional(),
    /**
     * What the answer did for the report's own brand: named it, named it
     * first, left it out (read, and not in it), or failed (no answer). Needs
     * `reportId`, which says whose brand; the server refuses it without one.
     */
    result: z.enum(["named", "first", "out", "failed"]).optional(),
    /** Opaque. Comes from `nextCursor`; never constructed by a caller. */
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * A page of rows.
 *
 * No total. Counting every matching row to render "1–50 of 312" costs a second
 * full scan on every page, and keyset pagination cannot produce one cheaply —
 * so the screen offers "load more" and does not claim a number it did not
 * measure.
 */
export const responseListSchema = z.object({
    rows: z.array(responseRowSchema),
    /** null when this is the last page. */
    nextCursor: z.string().nullable(),
});
export const detailCitationSchema = z.object({
    position: z.number().int(),
    rank: z.number().int(),
    url: z.string(),
    /**
     * Registrable domain, or null when the engine hid the destination behind an
     * opaque redirect. Null is a measurement — "it cited something and would not
     * say what" — not a gap waiting to be filled in.
     */
    domain: z.string().nullable(),
    title: z.string().nullable(),
    /**
     * The publisher's own name, as the engine printed it: "BikeWale", "The
     * Economic Times". Null when the engine named no publisher, and for anything
     * captured before this was kept — the screen then shows the domain, which is
     * true, rather than a brand name derived from it, which would be a guess.
     */
    sourceName: z.string().nullable(),
    inline: z.boolean(),
    /**
     * Where this link sits in the answer text, or null when we cannot say.
     *
     * Null is the normal case for anything captured before the renderer started
     * recording offsets, and for every engine whose answer text is trimmed after
     * rendering. A source is shown in the sentence that carried it only when this
     * is a number — never on a guess.
     */
    textIndex: z.number().int().min(0).nullable(),
});
/**
 * How the answer spoke about this brand, when a model has read it.
 *
 * The only modelled value in the product, and it says so. `evidence` is a span
 * of the answer verified to appear in it verbatim before the row was written,
 * and `evidenceIndex` is where we found it — not where the model said it was.
 * A reader can always go and disagree with the sentence (AGENTS.md §14a.2).
 */
export const detailSentimentSchema = z.object({
    label: z.enum(["positive", "neutral", "negative"]),
    evidence: z.string(),
    evidenceIndex: z.number().int().min(0),
    /** Which model said it, so a regression traces to a version (§14a.6). */
    model: z.string(),
});
export const detailMentionSchema = rowMentionSchema.extend({
    /**
     * The brand's own website, for its logo, sent with the answer so no screen
     * has to know it. Null only if the brand row is gone: a letter, not a guess.
     */
    domain: z.string().nullable(),
    /** Character offset of the first mention, so the answer can be marked up. */
    firstIndex: z.number().int().min(0),
    /**
     * null means nobody has read this answer for tone — either no model is
     * configured, or the model's quote could not be found in the answer and the
     * judgement was discarded with it. It never means "neutral".
     */
    sentiment: detailSentimentSchema.nullable(),
});
export const detailFanoutSchema = z.object({
    position: z.number().int(),
    query: z.string(),
    /** `observed` — the engine disclosed it — or `generated`, our prediction. */
    source: z.string(),
});
/** One sealed file, with the hash that makes it evidence rather than a blob. */
export const detailArtifactSchema = z.object({
    kind: z.string(),
    sha256: z.string(),
    bytes: z.number().int().min(0),
});
/**
 * The answer's shape, as the parser read it from the sealed DOM.
 *
 * A closed vocabulary, validated on the way out rather than trusted: this is
 * stored as JSON and JSON columns are where a shape quietly drifts. A block the
 * schema does not know fails here, at the boundary, instead of arriving at a
 * renderer that has no case for it.
 *
 * Recursion is avoided on purpose. Lists hold inline runs, not blocks, and a
 * list's points go one level down at most (`nested`), so the tree can never
 * nest far enough to need a depth guard.
 */
const inlineSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("text"), text: z.string() }),
    z.object({ kind: z.literal("strong"), text: z.string() }),
    z.object({ kind: z.literal("em"), text: z.string() }),
    z.object({ kind: z.literal("link"), text: z.string(), href: z.string() }),
    /**
     * A source marker the engine printed rather than words it wrote: Google's
     * `[[1]](url)` chips and the empty `[](url)` that follows a video card.
     *
     * It has no `text`, and that is the point. These characters are a pointer to
     * a source, so counting them as answer text would let a publisher's name in a
     * chip read as the engine naming a company. The screen draws the source's own
     * mark instead, at the place the engine put it — which is also the only thing
     * that can place a Google citation in a sentence.
     */
    z.object({
        kind: z.literal("cite"),
        /** The number the engine printed, or "" for an unnumbered chip. */
        label: z.string(),
        href: z.string(),
    }),
]);
export const answerBlockSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("heading"),
        level: z.union([z.literal(2), z.literal(3)]),
        inline: z.array(inlineSchema),
    }),
    z.object({ kind: z.literal("paragraph"), inline: z.array(inlineSchema) }),
    z.object({
        kind: z.literal("list"),
        ordered: z.boolean(),
        /**
         * How far this list is indented under the one before it, 0 at the top.
         *
         * Nesting is expressed as a depth on a sibling block rather than as a list
         * inside a list, so the vocabulary stays two levels deep and no renderer
         * needs a recursion guard. Absent on anything captured before nesting was
         * read, which reads as 0 — the shape those answers were stored in.
         */
        depth: z.number().int().min(0).max(3).default(0),
        items: z.array(z.array(inlineSchema)),
        /**
         * Where a numbered list's count begins, when points under a pick split it
         * into several blocks. Absent means 1, which is every answer read before.
         */
        start: z.number().int().min(1).optional(),
        /**
         * The points under each item, parallel to `items`: `nested[i]` sits under
         * `items[i]`, and is empty when there is none. One level, never deeper.
         *
         * Absent on every answer read before it existed, which draws exactly as it
         * always did. ChatGPT numbers its picks and bullets the reasons under each;
         * without this the reasons swallowed the next pick's heading.
         */
        nested: z.array(z.array(z.array(inlineSchema))).optional(),
    }),
    z.object({ kind: z.literal("quote"), inline: z.array(inlineSchema) }),
    z.object({
        kind: z.literal("table"),
        head: z.array(z.string()),
        rows: z.array(z.array(z.string())),
    }),
    /**
     * A picture the engine showed. `src` is the engine's own host — Google's
     * `gstatic.com` thumbnails and DataForSEO's image CDN — so a reader's browser
     * fetches it from them, which the data inventory records.
     */
    z.object({
        kind: z.literal("image"),
        src: z.string(),
        /** The engine's own alt text, or "" when it gave none. */
        alt: z.string(),
    }),
    /**
     * A video card, as Google draws one inside an AI Overview.
     *
     * Kept as one block rather than the poster, thumbnail, duration, title and
     * channel line it is made of, because those five pieces are one thing to a
     * reader and five pieces of litter to anyone scrolling past them.
     */
    z.object({
        kind: z.literal("video"),
        href: z.string(),
        title: z.string(),
        /** Poster frame, or null when the card had none. */
        thumbnail: z.string().nullable(),
        /** As the engine printed it, e.g. `0:51`. Null when it printed none. */
        duration: z.string().nullable(),
        /** The line under the card, e.g. `YouTube · MotorInc`. Null when absent. */
        source: z.string().nullable(),
    }),
]);
export const responseDetailSchema = z.object({
    row: responseRowSchema,
    /** null when nothing was parsed. Never an empty string standing in for it. */
    answerText: z.string().nullable(),
    /**
     * Where each cited link's own text sits in `answerText`.
     *
     * ChatGPT prints its sources into the prose, so these characters are a link's
     * label rather than something the engine said. The screen draws them as a
     * chip instead of as a sentence, which is both what they are and what stops
     * them reading as the engine naming a company.
     *
     * Empty means "we cannot say" — not "there are none". Google's parser cannot
     * place its links, and every Google answer keeps its sources in a rail that
     * is removed before the text is read.
     */
    citedSpans: z.array(z.object({ start: z.number().int().min(0), length: z.number().int().min(1) })),
    /**
     * The same answer with its headings and lists intact.
     *
     * Empty for a capture parsed before this existed — which is a different state
     * from "the answer had no structure", and the screen says so rather than
     * rendering a blank panel.
     */
    answerBlocks: z.array(answerBlockSchema),
    citations: z.array(detailCitationSchema),
    mentions: z.array(detailMentionSchema),
    fanout: z.array(detailFanoutSchema),
    /**
     * Whether we could read what the engine searched for.
     *
     * `fanout: []` means two opposite things without this, and the screen has to
     * say which: an engine that did not search is a finding, and an engine we
     * could not read is a defect in our selector. Null for a capture parsed
     * before the distinction was stored — a third state, and the screen says that
     * too rather than guessing one of the other two.
     */
    fanoutRead: z.boolean().nullable(),
    artifacts: z.array(detailArtifactSchema),
    manifestSha256: z.string().nullable(),
});
//# sourceMappingURL=responses.js.map